import { ContentMasterProductView } from '../api/generated/models/ContentMasterProductView';

/**
 * Single source of truth for rendering a `ContentMasterProductView.processingStatus`
 * value in the UI. Returns the matching CSS modifier class.
 *
 * Palette:
 *   - `done` / `bulk_generate_done` → green (`--done`)
 *   - `in_progress` / `bulk_generate_in_progress` / `bulk_apply_in_progress` → blue (`--in-progress`)
 *   - `listing_generate_done` → blue too: the SCX listing is only a DRAFT
 *     (not applied to Wawi yet), so it reads as "still being worked on"
 *   - `bulk_generate_queued` / `bulk_apply_queued` → grey (`--queued`)
 *   - `bulk_generate_failed` / `bulk_apply_failed` → red (`--failed`)
 *   - everything else (incl. `new`, null, undefined) → neutral (`--new`)
 *
 * Used by the Content Master list page, the bulk-review page and the single
 * product page so the pill always looks the same regardless of where it is
 * rendered.
 */
export const getContentMasterStatusClassName = (
  // Widened to `string`: some statuses (`rejected_by_user`, `listing_apply_*`)
  // are not in the generated `processingStatus` enum yet.
  status?: ContentMasterProductView.processingStatus | string | null
): string => {
  switch (status) {
    // Not in the generated enum yet - handled as a string literal.
    case 'rejected_by_user':
      return 'content-master-status content-master-status--rejected';
    case ContentMasterProductView.processingStatus.DONE:
    case ContentMasterProductView.processingStatus.BULK_GENERATE_DONE:
      return 'content-master-status content-master-status--done';
    case ContentMasterProductView.processingStatus.IN_PROGRESS:
    case ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS:
    case ContentMasterProductView.processingStatus.BULK_APPLY_IN_PROGRESS:
    case ContentMasterProductView.processingStatus.LISTING_GENERATE_IN_PROGRESS:
    case ContentMasterProductView.processingStatus.LISTING_GENERATE_DONE:
      return 'content-master-status content-master-status--in-progress';
    case ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED:
    case ContentMasterProductView.processingStatus.BULK_APPLY_QUEUED:
    case ContentMasterProductView.processingStatus.LISTING_GENERATE_QUEUED:
      return 'content-master-status content-master-status--queued';
    case ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED:
    case ContentMasterProductView.processingStatus.BULK_APPLY_FAILED:
    case ContentMasterProductView.processingStatus.LISTING_GENERATE_FAILED:
      return 'content-master-status content-master-status--failed';
    default:
      return 'content-master-status content-master-status--new';
  }
};

/**
 * Maps a `processingStatus` enum value to the i18n key for its localized
 * label. Mirrors {@link getContentMasterStatusClassName} - same status set,
 * unknown / null / undefined fall back to `..._new`.
 *
 * `bulkOperation` (from `ContentMasterProductView.bulkOperation` /
 * `ContentMasterBulkReviewItemView.bulkOperation`) tells a TRANSLATE task
 * apart from a generate one - bulk translation reuses the `bulk_generate_*`
 * statuses, so with `translate_all_fields` those four statuses get
 * translate-specific labels instead. The CSS class is unchanged.
 */
export const contentMasterStatusLabelKey = (
  status?: ContentMasterProductView.processingStatus | string | null,
  bulkOperation?: ContentMasterProductView.bulkOperation | string | null
): string => {
  if (bulkOperation === ContentMasterProductView.bulkOperation.TRANSLATE_ALL_FIELDS) {
    switch (status) {
      case ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED:
        return 'content_master_processing_status_bulk_translate_queued';
      case ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS:
        return 'content_master_processing_status_bulk_translate_in_progress';
      case ContentMasterProductView.processingStatus.BULK_GENERATE_DONE:
        return 'content_master_processing_status_bulk_translate_done';
      case ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED:
        return 'content_master_processing_status_bulk_translate_failed';
      default:
        break;
    }
  }
  switch (status) {
    // Not in the generated enum yet - handled as a string literal.
    case 'rejected_by_user':
      return 'content_master_processing_status_rejected_by_user';
    case ContentMasterProductView.processingStatus.DONE:
      return 'content_master_processing_status_done';
    case ContentMasterProductView.processingStatus.IN_PROGRESS:
      return 'content_master_processing_status_in_progress';
    case ContentMasterProductView.processingStatus.BULK_GENERATE_QUEUED:
      return 'content_master_processing_status_bulk_generate_queued';
    case ContentMasterProductView.processingStatus.BULK_GENERATE_IN_PROGRESS:
      return 'content_master_processing_status_bulk_generate_in_progress';
    case ContentMasterProductView.processingStatus.BULK_GENERATE_DONE:
      return 'content_master_processing_status_bulk_generate_done';
    case ContentMasterProductView.processingStatus.BULK_GENERATE_FAILED:
      return 'content_master_processing_status_bulk_generate_failed';
    case ContentMasterProductView.processingStatus.BULK_APPLY_QUEUED:
      return 'content_master_processing_status_bulk_apply_queued';
    case ContentMasterProductView.processingStatus.BULK_APPLY_IN_PROGRESS:
      return 'content_master_processing_status_bulk_apply_in_progress';
    case ContentMasterProductView.processingStatus.BULK_APPLY_FAILED:
      return 'content_master_processing_status_bulk_apply_failed';
    case ContentMasterProductView.processingStatus.LISTING_GENERATE_QUEUED:
      return 'content_master_processing_status_listing_generate_queued';
    case ContentMasterProductView.processingStatus.LISTING_GENERATE_IN_PROGRESS:
      return 'content_master_processing_status_listing_generate_in_progress';
    case ContentMasterProductView.processingStatus.LISTING_GENERATE_DONE:
      return 'content_master_processing_status_listing_generate_done';
    case ContentMasterProductView.processingStatus.LISTING_GENERATE_FAILED:
      return 'content_master_processing_status_listing_generate_failed';
    default:
      return 'content_master_processing_status_new';
  }
};
